import {
  leadsRetrieveQueryKey,
  leadsTasksListQueryKey,
  opportunitiesLogsListQueryKey,
  opportunitiesTasksListQueryKey,
  ReturnTaskReadable,
  tasksListInfiniteQueryKey,
  tasksPartialUpdateMutation
} from '@crm/api'
import { SkeletonInputWrapper, Text } from '@shared/components'
import { useInvalidatePredicateQueries } from '@shared/hooks'
import { displayDate, showApiErrors } from '@shared/utils'
import { useMutation } from '@tanstack/react-query'
import { App, Card, Checkbox, Col, Divider, Flex, Row, theme } from 'antd'
import { Clock } from 'iconsax-react'
import { useTranslation } from 'react-i18next'

import { useStyle } from './styles'

type Props = {
  task: ReturnTaskReadable
}

export const OpportunityTaskItem = ({ task }: Props) => {
  const { styles } = useStyle()
  const { t } = useTranslation()
  const { token } = theme.useToken()
  const { message } = App.useApp()
  const { invalidateQueries } = useInvalidatePredicateQueries()

  const { mutate, isPending, variables } = useMutation({
    ...tasksPartialUpdateMutation(),
    onSuccess: () => {
      invalidateQueries([
        tasksListInfiniteQueryKey(),
        leadsRetrieveQueryKey({ path: { id: Number(task?.lead?.id) } }),
        leadsTasksListQueryKey({
          path: { lead_id: String(task?.lead?.id) },
          query: { completed: true }
        }),
        opportunitiesTasksListQueryKey({
          path: { opportunity_id: String(task?.opportunity?.id) },
          query: { completed: true }
        }),
        opportunitiesLogsListQueryKey({
          path: { opportunity_id: String(task?.opportunity?.id) }
        })
      ])

      message.success(t('success.success_data_save'))
    },
    onError: (e) => showApiErrors(e)
  })

  const onClickComplete = () => {
    if (!task?.id) return

    mutate({
      path: { id: task.id },
      body: { completed_at: new Date().toISOString() }
    })
  }

  return (
    <SkeletonInputWrapper
      height={46}
      active={isPending && variables?.path?.id === task?.id}
      className="w-full"
    >
      <Flex justify="space-between">
        <Flex align="center" gap={8} className={styles.taskItem}>
          <Card
            className={styles.checkbox}
            onClick={(e) => e.stopPropagation()}
          >
            <Checkbox onClick={onClickComplete} />
          </Card>

          <Divider className={styles.divider} type="vertical" />

          <Row gutter={[4, 4]}>
            <Col span={24}>
              <Flex gap={4} align="center">
                <Clock color={token.colorWarningActive} size={14} />

                <Text isEllipsis size={12} color="colorWarningActive">
                  {t('until')} {displayDate(task?.deadline ?? '')}
                </Text>
              </Flex>
            </Col>

            <Col span={24}>
              <Text isEllipsis>{task?.title}</Text>
            </Col>
          </Row>
        </Flex>
      </Flex>
    </SkeletonInputWrapper>
  )
}
